import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { fmtNum } from "@/utils/toEnglishDigits";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Warning,
  Database,
  Trash,
  X,
  Spinner,
  CheckCircle,
  Users,
  Car,
  Path,
  Coins,
  Bell,
  Files,
  ClockCounterClockwise,
  User,
  Prohibit,
  Package,
} from "@phosphor-icons/react";

// Icon per section key — keeps the list visually scannable.
const SECTION_ICONS = {
  bookings: <Package size={18} weight="duotone" />,
  companies: <Users size={18} weight="duotone" />,
  drivers: <User size={18} weight="duotone" />,
  external_drivers: <Users size={18} weight="duotone" />,
  cars: <Car size={18} weight="duotone" />,
  fleet_vehicles: <Car size={18} weight="duotone" />,
  driver_wage_rules: <Coins size={18} weight="duotone" />,
  external_driver_wage_rules: <Coins size={18} weight="duotone" />,
  route_rules: <Path size={18} weight="duotone" />,
  company_pricing: <Coins size={18} weight="duotone" />,
  driver_transactions: <Files size={18} weight="duotone" />,
  change_requests: <ClockCounterClockwise size={18} weight="duotone" />,
  notifications: <Bell size={18} weight="duotone" />,
  notification_settings: <Bell size={18} weight="duotone" />,
  date_overrides: <ClockCounterClockwise size={18} weight="duotone" />,
  counters: <Database size={18} weight="duotone" />,
};

export default function DataReset() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const [sectionModal, setSectionModal] = useState(null); // {key, label, count}
  const [nuclearOpen, setNuclearOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busyKey, setBusyKey] = useState(null); // key currently being wiped

  const isSuperAdmin = useMemo(() => {
    const email = (user?.email || "").toLowerCase();
    return email === "abugena01@gmail.com" || user?.is_super_admin === true;
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-reset/stats");
      setSections(Array.isArray(data?.sections) ? data.sections : []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      toast.error("تعذّر تحميل الإحصاءات: " + formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) load();
    else setLoading(false);
  }, [isSuperAdmin, load]);

  // Block non-super-admins with a full-page notice.
  if (!isSuperAdmin && !loading) {
    return (
      <div className="min-h-screen bg-background text-white" dir="rtl">
        <TopBar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex flex-col items-center gap-3 p-8 rounded-2xl border border-red-500/40 bg-red-500/10">
            <Prohibit size={48} weight="duotone" className="text-red-400" />
            <div className="text-red-300 font-bold text-lg">صلاحية غير كافية</div>
            <div className="text-white/60 text-sm">هذه الصفحة مقتصرة على المدير العام فقط.</div>
            <button
              onClick={() => navigate("/admin")}
              className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 border border-white/15 text-white/80 hover:bg-white/15"
              data-testid="data-reset-forbidden-back"
            >
              <ArrowLeft size={14} /> العودة للوحة الإدارة
            </button>
          </div>
        </main>
      </div>
    );
  }

  async function resetSection(section) {
    if (confirmText !== "تصفير") {
      toast.error('اكتب "تصفير" للتأكيد');
      return;
    }
    setBusyKey(section.key);
    try {
      const { data } = await api.post(`/admin/data-reset/${section.key}`, { confirm: "تصفير" });
      toast.success(`تم حذف ${fmtNum(data.deleted)} سجل من قسم "${data.label}"`);
      setSectionModal(null);
      setConfirmText("");
      load();
    } catch (err) {
      toast.error("تعذّر التصفير: " + formatApiError(err));
    } finally {
      setBusyKey(null);
    }
  }

  async function resetAll() {
    if (confirmText !== "تصفير الكل") {
      toast.error('اكتب "تصفير الكل" للتأكيد');
      return;
    }
    setBusyKey("__nuclear__");
    try {
      const { data } = await api.post("/admin/data-reset/all/nuclear", { confirm: "تصفير الكل" });
      const totalDeleted = (data?.results || []).reduce((n, r) => n + Number(r.deleted || 0), 0);
      toast.success(`تم تصفير كل الأقسام: ${fmtNum(totalDeleted)} سجل`);
      setNuclearOpen(false);
      setConfirmText("");
      load();
    } catch (err) {
      toast.error("تعذّر التصفير الكامل: " + formatApiError(err));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="min-h-screen bg-background text-white" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"
              data-testid="data-reset-back-btn"
            >
              <ArrowLeft size={16} className="text-white/70" />
            </button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2 text-red-400">
                <Trash size={22} weight="duotone" />
                تصفير البيانات
              </h1>
              <p className="text-white/50 text-xs mt-1">مقتصرة على المدير العام</p>
            </div>
          </div>
        </div>

        {/* Warning banner */}
        <div className="p-4 rounded-2xl border border-red-500/50 bg-red-500/10 flex items-start gap-3" data-testid="data-reset-warning">
          <Warning size={22} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-red-100 leading-relaxed">
            <div className="font-bold text-red-300 mb-1">تنبيه خطير</div>
            <div>
              هذه الصفحة تحذف البيانات نهائياً من قاعدة البيانات. لا يمكن التراجع عن العملية.
              حسابات الأدمن والإعدادات المحمية محفوظة تلقائياً.
            </div>
          </div>
        </div>

        {/* Total card */}
        <div className="p-4 rounded-2xl border border-primary/25 bg-primary/5 flex items-center gap-4" data-testid="data-reset-total-card">
          <div className="p-3 rounded-xl bg-primary/15 border border-primary/30">
            <Database size={22} weight="duotone" className="text-primary" />
          </div>
          <div className="flex-1">
            <div className="text-white/60 text-xs">إجمالي السجلات الحالية</div>
            <div className="num text-white font-black text-3xl" dir="ltr" data-testid="data-reset-total">
              {loading ? "…" : fmtNum(total)}
            </div>
          </div>
        </div>

        {/* Sections list */}
        <section className="bg-[#0F2148]/70 border border-primary/20 rounded-2xl overflow-hidden" data-testid="data-reset-sections">
          {loading ? (
            <div className="p-10 text-center text-white/50 flex items-center justify-center gap-2">
              <Spinner size={16} className="animate-spin" /> جاري التحميل…
            </div>
          ) : sections.length === 0 ? (
            <div className="p-10 text-center text-white/50">لا توجد أقسام قابلة للتصفير.</div>
          ) : (
            <div className="divide-y divide-primary/10">
              {sections.map((s) => {
                const empty = Number(s.count) === 0;
                return (
                  <div
                    key={s.key}
                    className="flex items-center gap-3 p-3 hover:bg-primary/[0.03]"
                    data-testid={`data-reset-row-${s.key}`}
                  >
                    <div className="p-2 rounded-lg bg-white/5 border border-white/10 text-primary">
                      {SECTION_ICONS[s.key] || <Database size={18} weight="duotone" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{s.label}</div>
                      <div className="text-white/40 text-[11px] mt-0.5 num" dir="ltr">
                        {s.collection}
                      </div>
                    </div>
                    <div className="text-white/70 text-sm font-bold num" dir="ltr" data-testid={`data-reset-count-${s.key}`}>
                      {fmtNum(s.count)}
                    </div>
                    <button
                      type="button"
                      disabled={empty || busyKey === s.key}
                      onClick={() => { setSectionModal(s); setConfirmText(""); }}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                        empty
                          ? "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
                          : "bg-red-500/15 border-red-500/40 text-red-300 hover:bg-red-500/25"
                      }`}
                      data-testid={`data-reset-btn-${s.key}`}
                    >
                      {busyKey === s.key ? <Spinner size={12} className="animate-spin" /> : <Trash size={12} weight="bold" />}
                      تصفير
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Nuclear button */}
        <button
          type="button"
          onClick={() => { setNuclearOpen(true); setConfirmText(""); }}
          disabled={loading || total === 0 || busyKey === "__nuclear__"}
          className="w-full p-4 rounded-2xl bg-gradient-to-br from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 border border-red-400/50 text-white font-black text-lg shadow-[0_8px_24px_rgba(239,68,68,0.35)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="data-reset-nuclear-btn"
        >
          {busyKey === "__nuclear__" ? <Spinner size={18} className="animate-spin" /> : <Warning size={20} weight="fill" />}
          تصفير الكل ({fmtNum(total)} سجل)
        </button>
      </main>

      {/* Section confirmation modal */}
      {sectionModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => busyKey !== sectionModal.key && setSectionModal(null)}>
          <div
            className="bg-[#0B1A3A] border border-red-500/40 rounded-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
            data-testid="data-reset-section-modal"
          >
            <div className="p-4 flex items-center justify-between border-b border-red-500/20">
              <h2 className="text-lg font-bold text-red-300 flex items-center gap-2">
                <Warning size={18} weight="fill" /> تصفير قسم
              </h2>
              <button type="button" onClick={() => setSectionModal(null)} className="p-1 rounded-lg hover:bg-white/10" data-testid="data-reset-section-close">
                <X size={18} className="text-white/60" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-white text-sm leading-relaxed">
                سيتم حذف كل السجلات في قسم <span className="text-primary font-bold">{sectionModal.label}</span> نهائياً.
              </div>
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-100 text-sm">
                عدد السجلات: <span className="num font-bold text-red-300" dir="ltr">{fmtNum(sectionModal.count)}</span>
              </div>
              <label className="block">
                <div className="text-white/70 text-xs mb-1">اكتب <b className="text-red-300">تصفير</b> للتأكيد</div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full bg-[#0B1A3A] border border-red-500/40 rounded-lg px-3 py-2 text-white text-center font-bold focus:outline-none focus:border-red-500"
                  placeholder="تصفير"
                  autoFocus
                  data-testid="data-reset-section-confirm-input"
                />
              </label>
            </div>
            <div className="p-4 border-t border-red-500/20 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSectionModal(null)}
                disabled={busyKey === sectionModal.key}
                className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 disabled:opacity-50"
                data-testid="data-reset-section-cancel"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={() => resetSection(sectionModal)}
                disabled={confirmText !== "تصفير" || busyKey === sectionModal.key}
                className="px-4 py-2 rounded-xl bg-red-600 text-white font-bold hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="data-reset-section-confirm"
              >
                {busyKey === sectionModal.key && <Spinner size={14} className="animate-spin" />}
                تصفير الآن
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nuclear confirmation modal */}
      {nuclearOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => busyKey !== "__nuclear__" && setNuclearOpen(false)}>
          <div
            className="bg-[#0B1A3A] border border-red-500/50 rounded-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
            data-testid="data-reset-nuclear-modal"
          >
            <div className="p-4 flex items-center justify-between border-b border-red-500/30 bg-red-500/10">
              <h2 className="text-lg font-black text-red-300 flex items-center gap-2">
                <Warning size={18} weight="fill" /> تصفير كامل
              </h2>
              <button type="button" onClick={() => setNuclearOpen(false)} className="p-1 rounded-lg hover:bg-white/10" data-testid="data-reset-nuclear-close">
                <X size={18} className="text-white/60" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-red-200 text-sm leading-relaxed">
                <b className="text-red-300">تحذير:</b> سيتم حذف كل الحجوزات والسائقين والشركات والسيارات وقواعد الأجرة وكل السجلات القابلة للتصفير في قاعدة البيانات. لا يمكن التراجع عن هذه العملية.
              </div>
              <div className="p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-100 text-sm">
                إجمالي السجلات التي ستُحذف: <span className="num font-black text-red-300" dir="ltr">{fmtNum(total)}</span>
              </div>
              <label className="block">
                <div className="text-white/70 text-xs mb-1">اكتب <b className="text-red-300">تصفير الكل</b> بالضبط للتأكيد</div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full bg-[#0B1A3A] border border-red-500/50 rounded-lg px-3 py-2 text-white text-center font-black focus:outline-none focus:border-red-500"
                  placeholder="تصفير الكل"
                  autoFocus
                  data-testid="data-reset-nuclear-confirm-input"
                />
              </label>
            </div>
            <div className="p-4 border-t border-red-500/30 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setNuclearOpen(false)}
                disabled={busyKey === "__nuclear__"}
                className="px-4 py-2 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 disabled:opacity-50"
                data-testid="data-reset-nuclear-cancel"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={resetAll}
                disabled={confirmText !== "تصفير الكل" || busyKey === "__nuclear__"}
                className="px-4 py-2 rounded-xl bg-red-600 text-white font-black hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="data-reset-nuclear-confirm"
              >
                {busyKey === "__nuclear__" && <Spinner size={14} className="animate-spin" />}
                نعم، تصفير الكل
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
